/**
 * Component: Newsletter Popup
 *
 * Description:
 * The popup will not be shown under the following conditions:
 * 1. If a user is already logged in.
 * 2. If the user has dismissed the newsletter popup.
 * 3. If the newsletter signup page counter is 0.
 *
 */

<template>
  <Teleport to="#main-content">
    <div
      v-if="showPopup"
      ref="container"
      class="newsletter-popup"
      :class="[
        `section-content--${content}`,
        { 'newsletter-popup--sticky': isSticky,
          'newsletter-popup--success': isSuccessful
        }
      ]"
      :style="`background-color: ${background}`"
    >
      <div class="newsletter-popup__container">
        <p
          v-show="showText"
          class="
          newsletter-popup__content
          text-desktop-regular-desktop
          text-mobile-regular-mobile
          rte
        "
          v-html="$string('general.newsletter_popup.content_html')"
        />

        <newsletter-form
          ref="newsletter"
          block-class="newsletter-popup"
          layout="popup"
          @newsletter-submitted="closeNewsletterPopup"
        />
      </div>

      <button
        v-show="showText"
        class="newsletter-popup__close-btn button__label"
        type="button"
        @click="closeNewsletterPopup(true)"
      >
        <icon-chevron-special-right />
        <span
          v-text="$string('general.newsletter_popup.close')"
        />
      </button>
    </div>
  </Teleport>
</template>

<script>

/**
 * Vue: Newsletter popup (newsletter-popup)
 * -----------------------------------------------------------------------------
 * Global modal for newsletter sign up form.
 *
 */
import Cookies from 'js-cookie'

import NewsletterForm from '~async/newsletter-form/newsletter-form'
import IconChevronSpecialRight from '~icons/direction/chevron/right-special.svg'

export default {
  name: 'NewsletterPopup',

  components: {
    NewsletterForm,
    IconChevronSpecialRight,
  },

  props: {
    background: {
      type: String,
      default: '',
    },
    content: {
      type: String,
      default: '',
    },
    customerId: {
      type: String,
      default: '',
    },
    isSticky: {
      type: Boolean,
      default: true,
    },
    pageNumber: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
      showPopup: false,
      showPopupForce: false,
      showText: true,
      pageCounter: '',
      isSuccessful: false,
    }
  },

  watch: {
    pageCounter() {
      if (this.pageCounter >= this.pageNumber) {
        this.showPopup = true

        this.$nextTick().then(() => {
          this.getContainerHeight()
          this.$refs.newsletter?.getNewsletterForm()
        })
      }
    },
  },

  mounted() {
    cnvs.EventBus.listen('newsletter-clicked', () => {
      this.showText = true
      this.showPopup = true
      this.isSuccessful = false
    })

    this.getContainerHeight()

    window.addEventListener(
      'load',
      this.handleNewsletterInit(),
    )
  },

  methods: {

    /**
     * Set newsletter height as root variable.
     */
    getContainerHeight() {
      const container = this.$refs.container

      if (!container) {
        return
      }

      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(() => {
          document.documentElement.style.setProperty(
            '--newsletter-popup-height',
            `${container.offsetHeight}px`,
          )
        })
      })

      resizeObserver.observe(container)
    },

    /**
     * Initialize newsletter popup.
     */
    handleNewsletterInit() {

      /**
       * Hides newsletter popup if modal is dismissed or newsletter page counter
       * is set to 0
       */
      if (Cookies.get('newsletter_modal') === 'dismissed' || this.pageNumber === 0) {
        return
      }

      /**
       * Hides newsletter popup if user is signed in
       */
      if (this.customerId) {
        this.closeNewsletterPopup()
        return
      }

      if (!Cookies.get('newsletter_page_counter')) {
        Cookies.set('newsletter_page_counter', '0')
      }

      this.pageCounter = Number(Cookies.get('newsletter_page_counter'))

      /**
       * Checks if number of pages visited is less than newsletter page counter
       * and increases the page counter cookie by 1
       * - Does not update page count on reload i.e. if the current URL is
       * equal to the previous url, the page counter will not be updated
       */
      if (this.pageCounter < this.pageNumber) {
        const currentUrl = window.location.pathname
        const previousUrl = sessionStorage.getItem('previousUrl')

        if (currentUrl === previousUrl) {
          return
        }

        this.pageCounter = ++this.pageCounter
        Cookies.set('newsletter_page_counter', this.pageCounter)
      }
    },

    /**
     * Handle close callback function by setting cookie.
     */
    closeNewsletterPopup(clicked = false) {
      Cookies.set('newsletter_modal', 'dismissed', { expires: 30 })
      Cookies.set('newsletter_page_counter', '0')
      this.showText = false
      if (clicked === true) {
        this.pageCounter = 0
        this.isSuccessful = clicked
      } else {
        setTimeout(() => {
          this.pageCounter = 0
          this.isSuccessful = true
        }, 3000)
      }
    },
  },
}
</script>

<style lang="scss">
@import "./newsletter-popup";
</style>
